import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Context from './index';
import axios from 'axios';
import { handleAddModifyOperation, fetchAnnouncementById, fetchAllAnnouncements } from '../../Api/ApiMethods';
import { toast } from 'react-toastify';

const AnnouncementApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/announcement';

const AnnouncementProvider = (props) => {
    const [allAnnouncements, setAllAnnouncements] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);

    // Duyuruları getiren fonksiyon (fetchAllAnnouncements kullanarak)
    const getAnnouncements = useCallback(async (params = {}) => { // Parametreleri doğrudan alalım
        setLoading(true);
        // fetchAllAnnouncements fonksiyonunu çağır
        const result = await fetchAllAnnouncements({
            apiEndpoint: AnnouncementApiUrl + "/getwithcount",
            params: params // Gelen page, pageSize, search vb. parametreleri ilet
        });

        // Dönen veriyi state'e set et
        if (result) {
            // Map etme işlemi fetchAllAnnouncements içinde yapıldığı için burada tekrar gerekmez.
            setAllAnnouncements(result.items);
            setTotalRows(result.total);
        } else {
            // Hata durumu fetchAllAnnouncements içinde handle edildi,
            // ama isterseniz burada ek işlem yapabilirsiniz.
            setAllAnnouncements([]);
            setTotalRows(0);
        }
        setLoading(false);
    }, []); // useCallback bağımlılığı kaldırıldı (endpoint sabit)

    useEffect(() => {
        // Bileşen yüklendiğinde duyuruları getir (parametre göndermeden, API varsayılanlarını kullanır)
        getAnnouncements();
    }, [getAnnouncements]);

    // Yeni duyuru ekleyen fonksiyon (handleAddModifyOperation kullanarak)
    const AddNewAnnouncement = async (data) => {
        setLoading(true);
        try {
            const apiData = {
                title: data.title,
                text: data.desc, // desc -> text
                isActive: data.isActive !== undefined ? data.isActive : true
            };
            await handleAddModifyOperation({
                apiEndpoint: AnnouncementApiUrl,
                mode: 'add',
                data: apiData,
                // Başarılı olunca listeyi yenilemek için getAnnouncements'ı çağır
                // (mevcut parametreleri korumak isterseniz state'te tutmanız gerekir)
                refreshDataCallback: () => getAnnouncements(),
                componentName: 'Duyuru',
            });
        } catch (error) {
           // Hata handle ediliyor
        } finally {
            setLoading(false);
        }
    };

    // Duyuru güncelleyen fonksiyon (handleAddModifyOperation kullanarak)
    const UpdateAnnouncement = async (id, data) => {
        setLoading(true);
        try {
            const apiData = {
                title: data.title,
                text: data.desc, // desc -> text
                isActive: data.isActive
            };
            await handleAddModifyOperation({
                apiEndpoint: AnnouncementApiUrl,
                selectedItem: { id },
                mode: 'modify',
                data: apiData,
                refreshDataCallback: () => getAnnouncements(), // Listeyi yenile
                componentName: 'Duyuru',
            });
        } catch (error) {
           // Hata handle ediliyor
        } finally {
            setLoading(false);
        }
    };

    // Duyuru silen fonksiyon (axios.delete ile)
    const RemoveAnnouncement = async (id) => {
        setLoading(true);
        try {
            await axios.delete(`${AnnouncementApiUrl}/${id}`);
            toast.success('Duyuru başarıyla silindi!');
            getAnnouncements(); // Listeyi yenile
        } catch (error) {
            console.error(`Error deleting announcement with id ${id}:`, error);
            toast.error(`Duyuru silinirken hata oluştu: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Tekil duyuru getirme fonksiyonu (fetchAnnouncementById kullanarak)
    const getSingleAnnouncement = useCallback(async (id) => {
        setLoading(true);
        try {
            const announcement = await fetchAnnouncementById({
                apiEndpoint: AnnouncementApiUrl,
                id: id
            });
            return announcement;
        } catch (error) {
             return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Context değerini useMemo ile optimize edelim
    const contextValue = useMemo(() => ({
        allAnnouncements,
        totalRows,
        loading,
        getAnnouncements, // Artık fetchAllAnnouncements'ı kullanıyor
        AddNewAnnouncement,
        UpdateAnnouncement,
        RemoveAnnouncement,
        getSingleAnnouncement
    }), [allAnnouncements, totalRows, loading, getAnnouncements, getSingleAnnouncement]);


    return (
        <Context.Provider value={contextValue}>
            {props.children}
        </Context.Provider>
    );
};

export default AnnouncementProvider;
